import React, { useEffect, useRef, useState } from "react";
import { Col, Form, OverlayTrigger, Popover, Row } from "react-bootstrap";
import { DeleteModal } from "CommonComponent";
import { NumericFormat } from "react-number-format";
import isEqual from "lodash/isEqual";

const createMiscTelecomRow = (id) => ({
  id,
  heading: "",
  charge: "",
});

const pickFirstValue = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }
  return "";
};

const toEditableValue = (value) =>
  value === undefined || value === null ? "" : value;

const normalizeChargeTimeType = (value) =>
  String(value || "").trim().toLowerCase() === "talk_time"
    ? "talk_time"
    : "handle_time";

const normalizeChargeTimeUnit = (value) =>
  String(value || "").trim().toLowerCase() === "hour"
    ? "hour"
    : "minute";

const pickTelecomSupport = (sectionData = {}) => {
  const support =
    sectionData.telecom_support && typeof sectionData.telecom_support === "object"
      ? sectionData.telecom_support
      : {};
  const legacy =
    sectionData.telecom && typeof sectionData.telecom === "object"
      ? sectionData.telecom
      : {};
  const source = Object.keys(support).length > 0 ? support : legacy;

  return {
    title: toEditableValue(source.title) || "Telecom",
    licenseRate: toEditableValue(source.licenseRate),
    numberOfLicenses: toEditableValue(
      pickFirstValue(
        source.numberOfLicenses,
        source.noOfLicenses,
        source.noOfLicense
      )
    ),
  };
};

const pickTelecomConsumption = (sectionData = {}) => {
  const consumption =
    sectionData.telecom_consumption &&
    typeof sectionData.telecom_consumption === "object"
      ? sectionData.telecom_consumption
      : {};
  const legacy =
    sectionData.telecom && typeof sectionData.telecom === "object"
      ? sectionData.telecom
      : {};
  const source = Object.keys(consumption).length > 0 ? consumption : legacy;

  return {
    rate: toEditableValue(pickFirstValue(source.rate, source.perMinuteRate)),
    chargeTimeType: normalizeChargeTimeType(
      source.chargeTimeType || source.charge_time_type
    ),
    chargeTimeUnit: normalizeChargeTimeUnit(
      source.chargeTimeUnit || source.charge_time_unit
    ),
    handleTime: toEditableValue(
      pickFirstValue(
        source.handleTime,
        source.handle_time,
        source.acdHandleTime,
        source.acd_handle_time
      )
    ),
    talkTime: toEditableValue(
      pickFirstValue(
        source.talkTime,
        source.talk_time,
        source.acdTalkTime,
        source.acd_talk_time
      )
    ),
    totalMinutes: toEditableValue(
      pickFirstValue(
        source.totalMinutes,
        source.total_minutes,
        source.acdTotalMinutes,
        source.acd_total_minutes
      )
    ),
  };
};

const formatRuntimeValue = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "";
  if (Number.isInteger(numeric)) return String(numeric);
  return numeric.toFixed(2);
};

const normalizeMiscTelecomRows = (rows = []) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return [createMiscTelecomRow(1), createMiscTelecomRow(2)];
  }

  return rows.map((row, index) => ({
    id: index + 1,
    heading: toEditableValue(row?.heading),
    charge: toEditableValue(row?.charge),
  }));
};

const createCrmSubscriptionRow = (id, data = {}) => ({
  subscriptionId: String(id || ""),
  title: "",
  crmSubscription: "",
  crmDateFrom: "",
  crmDateTo: "",
  isPaid: false,
  isLocked: false,
  canEdit: true,
  canDelete: true,
  lockReason: "",
  ...data,
});

const toBoolean = (value, fallback = false) => {
  if (value === undefined || value === null) return fallback;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  const normalized = String(value).trim().toLowerCase();
  if (["true", "1", "yes"].includes(normalized)) return true;
  if (["false", "0", "no"].includes(normalized)) return false;
  return fallback;
};

const toLockReason = (value) => {
  if (value === undefined || value === null) return "";
  if (Array.isArray(value)) return String(value[0] || "");
  return String(value);
};

const normalizeCrmSubscriptionRows = (crmData = null) => {
  if (Array.isArray(crmData) && crmData.length > 0) {
    return crmData.map((row, index) => {
      const fallbackId = `crm-local-${index + 1}`;
      const subscriptionId =
        row?.subscriptionId || row?.id || row?.uuid || fallbackId;

      return createCrmSubscriptionRow(subscriptionId, {
        subscriptionId: String(subscriptionId),
        title: toEditableValue(row?.title),
        crmSubscription: toEditableValue(row?.crmSubscription),
        crmDateFrom: toEditableValue(row?.crmDateFrom),
        crmDateTo: toEditableValue(row?.crmDateTo),
        isPaid: toBoolean(row?.isPaid, false),
        isLocked: toBoolean(row?.isLocked, false),
        canEdit: toBoolean(row?.canEdit, true),
        canDelete: toBoolean(row?.canDelete, true),
        lockReason: toLockReason(row?.lockReason),
      });
    });
  }

  if (crmData && typeof crmData === "object") {
    const primaryId = crmData?.subscriptionId || crmData?.id || "crm-local-1";
    const primaryRow = createCrmSubscriptionRow(primaryId, {
      subscriptionId: String(primaryId),
      title: toEditableValue(crmData.title) || "CRM Subscription",
      crmSubscription: toEditableValue(crmData.crmSubscription),
      crmDateFrom: toEditableValue(crmData.crmDateFrom),
      crmDateTo: toEditableValue(crmData.crmDateTo),
      isPaid: toBoolean(crmData.isPaid, false),
      isLocked: toBoolean(crmData.isLocked, false),
      canEdit: toBoolean(crmData.canEdit, true),
      canDelete: toBoolean(crmData.canDelete, true),
      lockReason: toLockReason(crmData.lockReason),
    });

    const hasSocialValues = Boolean(
      crmData.socialCrmTitle ||
      crmData.socialCrmSubscription ||
      crmData.socialCrmDateFrom ||
      crmData.socialCrmDateTo
    );

    if (!hasSocialValues) {
      return [primaryRow];
    }

    return [
      primaryRow,
      createCrmSubscriptionRow("crm-local-2", {
        subscriptionId: "crm-local-2",
        title: toEditableValue(crmData.socialCrmTitle) || "Social CRM Subscription",
        crmSubscription: toEditableValue(crmData.socialCrmSubscription),
        crmDateFrom: toEditableValue(crmData.socialCrmDateFrom),
        crmDateTo: toEditableValue(crmData.socialCrmDateTo),
        isPaid: false,
        isLocked: false,
        canEdit: true,
        canDelete: true,
        lockReason: "",
      }),
    ];
  }

  return [
    createCrmSubscriptionRow("crm-local-1", {
      subscriptionId: "crm-local-1",
      title: "CRM Subscription",
    }),
  ];
};

const CurrencyInput = ({
  value,
  onChange,
  isInvalid,
  disabled = false,
  className = "input-field",
}) => (
  <NumericFormat
    value={value ?? ""}
    onValueChange={(values) => onChange?.({ target: { value: values.value } })}
    thousandSeparator
    decimalScale={2}
    allowNegative={false}
    prefix="$ "
    placeholder="$ 0.00"
    customInput={Form.Control}
    isInvalid={isInvalid}
    disabled={disabled}
    className={className}
  />
);

const TechnologyTab = ({
  selectedSections = [],
  onSectionDataChange,
  initialSectionData = {},
  getFieldError = () => "",
}) => {
  const shouldShow = (sectionId) => selectedSections.includes(sectionId);
  const hasSelectedSection = selectedSections.length > 0;

  const initialCrmRows = normalizeCrmSubscriptionRows(
    initialSectionData.crm_subscription_annual
  );
  const initialTelecomSupport = pickTelecomSupport(initialSectionData);
  const initialTelecomConsumption = pickTelecomConsumption(initialSectionData);

  const [crmSubscriptionRows, setCrmSubscriptionRows] = useState(initialCrmRows);
  const nextCrmSubscriptionIdRef = useRef(initialCrmRows.length + 1);

  const [telecomLicenseRate, setTelecomLicenseRate] = useState(
    initialTelecomSupport.licenseRate
  );
  const [telecomTitle, setTelecomTitle] = useState(initialTelecomSupport.title);
  const [telecomPerMinuteRate, setTelecomPerMinuteRate] = useState(
    initialTelecomConsumption.rate
  );
  const [telecomChargeTimeType, setTelecomChargeTimeType] = useState(
    initialTelecomConsumption.chargeTimeType
  );
  const [telecomChargeTimeUnit, setTelecomChargeTimeUnit] = useState(
    initialTelecomConsumption.chargeTimeUnit
  );
  const [telecomNumberOfLicenses, setTelecomNumberOfLicenses] = useState(
    initialTelecomSupport.numberOfLicenses
  );

  const [telecomHandleTime, setTelecomHandleTime] = useState(
    initialTelecomConsumption.handleTime
  );
  const [telecomTalkTime, setTelecomTalkTime] = useState(
    initialTelecomConsumption.talkTime
  );
  const [telecomTotalMinutes, setTelecomTotalMinutes] = useState(
    initialTelecomConsumption.totalMinutes
  );

  const [miscTelecomRows, setMiscTelecomRows] = useState(
    normalizeMiscTelecomRows(initialSectionData.misc_telecom)
  );
  const nextMiscTelecomIdRef = useRef(
    normalizeMiscTelecomRows(initialSectionData.misc_telecom).length + 1
  );
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const oldInitialSectionDataRef = useRef(initialSectionData);
  const lastEmittedPayloadRef = useRef(null);
  const isHydratingRef = useRef(false);
  const skipNextEmitRef = useRef(false);
  const radioScopeRef = useRef(
    `telecom-${Math.random().toString(36).slice(2, 10)}`
  );

  const onSectionDataChangeRef = useRef(onSectionDataChange);
  useEffect(() => {
    onSectionDataChangeRef.current = onSectionDataChange;
  }, [onSectionDataChange]);

  useEffect(() => {
    const hasIncomingData =
      initialSectionData && Object.keys(initialSectionData).length > 0;

    if (!hasIncomingData) {
      return;
    }

    if (isEqual(oldInitialSectionDataRef.current, initialSectionData)) {
      return;
    }

    const nextCrmRows = normalizeCrmSubscriptionRows(
      initialSectionData.crm_subscription_annual
    );
    const nextTelecomSupport = pickTelecomSupport(initialSectionData);
    const nextTelecomConsumption = pickTelecomConsumption(initialSectionData);
    const nextMiscRows = normalizeMiscTelecomRows(initialSectionData.misc_telecom);

    const nextTelecomTitle = nextTelecomSupport.title;
    const nextTelecomLicenseRate = nextTelecomSupport.licenseRate;
    const nextTelecomPerMinuteRate = nextTelecomConsumption.rate;
    const nextTelecomChargeTimeType = nextTelecomConsumption.chargeTimeType;
    const nextTelecomChargeTimeUnit = nextTelecomConsumption.chargeTimeUnit;
    const nextTelecomNumberOfLicenses = nextTelecomSupport.numberOfLicenses;
    const nextTelecomHandleTime = nextTelecomConsumption.handleTime;
    const nextTelecomTalkTime = nextTelecomConsumption.talkTime;
    const nextTelecomTotalMinutes = nextTelecomConsumption.totalMinutes;

    const nextHydratedSnapshot = {
      crmSubscriptionRows: nextCrmRows.map((row) => ({
        subscriptionId: row.subscriptionId,
        title: row.title,
        crmSubscription: row.crmSubscription,
        crmDateFrom: row.crmDateFrom,
        crmDateTo: row.crmDateTo,
        isPaid: row.isPaid,
        isLocked: row.isLocked,
        canEdit: row.canEdit,
        canDelete: row.canDelete,
        lockReason: row.lockReason,
      })),
      telecomTitle: nextTelecomTitle,
      telecomLicenseRate: nextTelecomLicenseRate,
      telecomPerMinuteRate: nextTelecomPerMinuteRate,
      telecomChargeTimeType: nextTelecomChargeTimeType,
      telecomChargeTimeUnit: nextTelecomChargeTimeUnit,
      telecomNumberOfLicenses: nextTelecomNumberOfLicenses,
      telecomHandleTime: nextTelecomHandleTime,
      telecomTalkTime: nextTelecomTalkTime,
      telecomTotalMinutes: nextTelecomTotalMinutes,
      miscTelecomRows: nextMiscRows.map((row) => ({
        heading: row.heading,
        charge: row.charge,
      })),
    };

    const currentHydratedSnapshot = {
      crmSubscriptionRows: crmSubscriptionRows.map((row) => ({
        subscriptionId: row.subscriptionId,
        title: row.title,
        crmSubscription: row.crmSubscription,
        crmDateFrom: row.crmDateFrom,
        crmDateTo: row.crmDateTo,
        isPaid: row.isPaid,
        isLocked: row.isLocked,
        canEdit: row.canEdit,
        canDelete: row.canDelete,
        lockReason: row.lockReason,
      })),
      telecomTitle,
      telecomLicenseRate,
      telecomPerMinuteRate,
      telecomChargeTimeType,
      telecomChargeTimeUnit,
      telecomNumberOfLicenses,
      telecomHandleTime,
      telecomTalkTime,
      telecomTotalMinutes,
      miscTelecomRows: miscTelecomRows.map((row) => ({
        heading: row.heading,
        charge: row.charge,
      })),
    };

    if (isEqual(currentHydratedSnapshot, nextHydratedSnapshot)) {
      oldInitialSectionDataRef.current = initialSectionData;
      return;
    }

    isHydratingRef.current = true;
    skipNextEmitRef.current = true;

    setCrmSubscriptionRows(nextCrmRows);
    setTelecomTitle(nextTelecomTitle);
    setTelecomLicenseRate(nextTelecomLicenseRate);
    setTelecomPerMinuteRate(nextTelecomPerMinuteRate);
    setTelecomChargeTimeType(nextTelecomChargeTimeType);
    setTelecomChargeTimeUnit(nextTelecomChargeTimeUnit);
    setTelecomNumberOfLicenses(nextTelecomNumberOfLicenses);
    setTelecomHandleTime(nextTelecomHandleTime);
    setTelecomTalkTime(nextTelecomTalkTime);
    setTelecomTotalMinutes(nextTelecomTotalMinutes);
    setMiscTelecomRows(nextMiscRows);

    nextCrmSubscriptionIdRef.current = nextCrmRows.length + 1;
    nextMiscTelecomIdRef.current = nextMiscRows.length + 1;
    oldInitialSectionDataRef.current = initialSectionData;
  }, [initialSectionData]);

  useEffect(() => {
    if (isHydratingRef.current || skipNextEmitRef.current) {
      isHydratingRef.current = false;
      skipNextEmitRef.current = false;
      return;
    }

    const crmSubscriptionPayloadRows = crmSubscriptionRows.map((row) => ({
      subscriptionId: String(row.subscriptionId || ""),
      title: row.title,
      crmSubscription: row.crmSubscription,
      crmDateFrom: row.crmDateFrom,
      crmDateTo: row.crmDateTo,
      isPaid: toBoolean(row.isPaid, false),
    }));
    const miscTelecomPayloadRows = miscTelecomRows.map(({ id, ...row }) => row);

    const nextPayload = {
      crm_subscription_annual: crmSubscriptionPayloadRows,
      telecom_support: {
        title: telecomTitle,
        licenseRate: telecomLicenseRate,
        numberOfLicenses: telecomNumberOfLicenses,
      },
      telecom_consumption: {
        rate: telecomPerMinuteRate,
        chargeTimeType: selectedChargeTimeType,
        chargeTimeUnit: selectedChargeTimeUnit,
        handleTime: telecomHandleTime,
        talkTime: telecomTalkTime,
        totalMinutes: telecomTotalMinutes,
      },
      misc_telecom: miscTelecomPayloadRows,
    };

    if (isEqual(lastEmittedPayloadRef.current, nextPayload)) {
      return;
    }

    lastEmittedPayloadRef.current = nextPayload;
    onSectionDataChangeRef.current?.(nextPayload);
  }, [
    crmSubscriptionRows,
    telecomLicenseRate,
    telecomTitle,
    telecomPerMinuteRate,
    telecomChargeTimeType,
    telecomChargeTimeUnit,
    telecomNumberOfLicenses,
    telecomHandleTime,
    telecomTalkTime,
    telecomTotalMinutes,
    miscTelecomRows,
  ]);

  const getError = (sectionId, field, rowIndex = null) =>
    getFieldError?.(sectionId, field, rowIndex) || "";

  const selectedChargeTimeType = normalizeChargeTimeType(telecomChargeTimeType);
  const selectedChargeTimeUnit = normalizeChargeTimeUnit(telecomChargeTimeUnit);
  const chargeTimeTypeName = `${radioScopeRef.current}-charge-time-type`;
  const chargeTimeUnitName = `${radioScopeRef.current}-charge-time-unit`;
  const handleTimeMinutes = Number(telecomHandleTime);
  const talkTimeMinutes = Number(telecomTalkTime);
  const selectedRuntimeMinutes =
    selectedChargeTimeType === "talk_time" ? talkTimeMinutes : handleTimeMinutes;
  const totalRuntimeDisplay = Number.isFinite(selectedRuntimeMinutes)
    ? selectedChargeTimeUnit === "hour"
      ? formatRuntimeValue(selectedRuntimeMinutes / 60)
      : formatRuntimeValue(selectedRuntimeMinutes)
    : "";

  const handleCrmFieldChange = (rowId, field) => (event) => {
    const { value } = event.target;
    setCrmSubscriptionRows((prev) =>
      prev.map((row) =>
        row.subscriptionId === rowId ? { ...row, [field]: value } : row
      )
    );
  };

  const handleCrmPaidChange = (rowId) => (event) => {
    const checked = Boolean(event?.target?.checked);
    setCrmSubscriptionRows((prev) =>
      prev.map((row) =>
        row.subscriptionId === rowId ? { ...row, isPaid: checked } : row
      )
    );
  };

  const handleAddCrmSubscriptionRow = () => {
    const nextId = nextCrmSubscriptionIdRef.current;
    nextCrmSubscriptionIdRef.current += 1;
    setCrmSubscriptionRows((prev) => [
      ...prev,
      createCrmSubscriptionRow(`crm-local-${nextId}`, {
        subscriptionId: `crm-local-${nextId}`,
        isPaid: false,
        isLocked: false,
        canEdit: true,
        canDelete: true,
        lockReason: "",
      }),
    ]);
  };

  const handleMiscTelecomFieldChange = (rowId, field) => (event) => {
    const { value } = event.target;
    setMiscTelecomRows((prev) =>
      prev.map((row) => (row.id === rowId ? { ...row, [field]: value } : row))
    );
  };

  const handleAddMiscTelecomRow = () => {
    const nextId = nextMiscTelecomIdRef.current;
    nextMiscTelecomIdRef.current += 1;
    setMiscTelecomRows((prev) => [...prev, createMiscTelecomRow(nextId)]);
  };

  const handleDeleteMiscTelecomRow = (rowId) => {
    setMiscTelecomRows((prev) => prev.filter((row) => row.id !== rowId));
  };

  const handleDeleteCrmSubscriptionRow = (rowId) => {
    setCrmSubscriptionRows((prev) =>
      prev.filter((row) => row.subscriptionId !== rowId)
    );
  };

  const handleDeleteClick = (type, rowId) => {
    setPendingDelete({ type, rowId });
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setPendingDelete(null);
  };

  const handleConfirmDelete = () => {
    if (!pendingDelete) {
      handleCloseDeleteModal();
      return;
    }

    if (pendingDelete.type === "crm_subscription") {
      handleDeleteCrmSubscriptionRow(pendingDelete.rowId);
    }

    if (pendingDelete.type === "misc_telecom") {
      handleDeleteMiscTelecomRow(pendingDelete.rowId);
    }

    handleCloseDeleteModal();
  };

  return (
    <div>
      <div className="labor-tab-section">
        {!hasSelectedSection && (
          <div className="invoice-section-div mb-0 text-center">
            <span className="font-16 font-weight-bold heading-color">
              First you have to select at least one section.
            </span>
          </div>
        )}

        {shouldShow("crm_subscription_annual") && (
          <div className="invoice-section-div">
            <h3 className="d-flex align-items-center gap-2">
              <img src="/assets/images/invoice-user-icon.svg" alt="" />
              <span className="font-16 font-weight-bold heading-color">
                CRM Subscriptions
              </span>
            </h3>
            {crmSubscriptionRows.map((row, rowIndex) => (
              <div
                className="bg-light p-3 border-sm mb-3"
                key={row.subscriptionId || rowIndex}
              >
                <Row>
                  {((row.isLocked || row.canEdit === false) && row.lockReason) && (
                    <Col lg={12}>
                      <p className="font-12 text-danger mb-2">{row.lockReason}</p>
                    </Col>
                  )}
                  <Col lg={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="label-input">Title</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Type here..."
                        value={row.title}
                        onChange={handleCrmFieldChange(row.subscriptionId, "title")}
                        isInvalid={Boolean(
                          getError("crm_subscription_annual", "title", rowIndex)
                        )}
                        disabled={row.isLocked || row.canEdit === false}
                        className="input-field"
                      />
                      <Form.Control.Feedback type="invalid">{getError("crm_subscription_annual", "title", rowIndex)}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col lg={5}>
                    <Form.Group className="mb-3">
                      <Form.Label className="label-input">CRM Subscription ($)</Form.Label>
                      <CurrencyInput
                        value={row.crmSubscription}
                        onChange={handleCrmFieldChange(row.subscriptionId, "crmSubscription")}
                        isInvalid={Boolean(
                          getError("crm_subscription_annual", "crmSubscription", rowIndex)
                        )}
                        disabled={row.isLocked || row.canEdit === false}
                      />
                      <Form.Control.Feedback type="invalid">{getError("crm_subscription_annual", "crmSubscription", rowIndex)}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col lg={1} className="text-center">
                    {row.canDelete !== false && !row.isLocked ? (
                      <button
                        type="button"
                        className="border-0 bg-transparent p-0"
                        onClick={() =>
                          handleDeleteClick("crm_subscription", row.subscriptionId)
                        }
                        aria-label="Delete CRM subscription row"
                      >
                        <img src="/assets/images/delete-icon.svg" alt="" />
                      </button>
                    ) : null}
                  </Col>
                  <Col lg={3}>
                    <Form.Group className="mb-0">
                      <Form.Label className="label-input">CRM Date From</Form.Label>
                      <Form.Control
                        type="date"
                        value={row.crmDateFrom}
                        onChange={handleCrmFieldChange(row.subscriptionId, "crmDateFrom")}
                        isInvalid={Boolean(
                          getError("crm_subscription_annual", "crmDateFrom", rowIndex)
                        )}
                        disabled={row.isLocked || row.canEdit === false}
                        className="input-field"
                      />
                      <Form.Control.Feedback type="invalid">{getError("crm_subscription_annual", "crmDateFrom", rowIndex)}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col lg={3}>
                    <Form.Group className="mb-0">
                      <Form.Label className="label-input">CRM Date To</Form.Label>
                      <Form.Control
                        type="date"
                        value={row.crmDateTo}
                        onChange={handleCrmFieldChange(row.subscriptionId, "crmDateTo")}
                        isInvalid={Boolean(
                          getError("crm_subscription_annual", "crmDateTo", rowIndex)
                        )}
                        disabled={row.isLocked || row.canEdit === false}
                        className="input-field"
                      />
                      <Form.Control.Feedback type="invalid">{getError("crm_subscription_annual", "crmDateTo", rowIndex)}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col lg={6}>
                    <Form.Group className="mb-0 mt-4">
                      <div className="d-flex align-items-center gap-2">
                        <Form.Check
                          type="checkbox"
                          id={`crm-already-paid-${row.subscriptionId || rowIndex}`}
                          label="Already paid"
                          checked={toBoolean(row.isPaid, false)}
                          onChange={handleCrmPaidChange(row.subscriptionId)}
                          disabled={row.isLocked || row.canEdit === false}
                        />
                        <OverlayTrigger
                          trigger={["hover", "focus"]}
                          placement="top"
                          overlay={
                            <Popover id={`crm-already-paid-help-${row.subscriptionId || rowIndex}`}>
                              <Popover.Body>
                                If this CRM charge was prepaid before the invoicing cycle started, check this. It will be treated as paid through the end of the cycle (crmDateTo).
                              </Popover.Body>
                            </Popover>
                          }
                        >
                          <button
                            type="button"
                            className="border-0 bg-transparent p-0 d-inline-flex"
                            aria-label="Already paid information"
                          >
                            <img
                              src="/assets/images/information-icon.svg"
                              alt=""
                              width={16}
                              height={16}
                            />
                          </button>
                        </OverlayTrigger>
                      </div>
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            ))}
            <button
              type="button"
              className="border-0 bg-transparent text-success font-12 font-weight-bold d-flex align-items-center gap-2"
              onClick={handleAddCrmSubscriptionRow}
            >
              <img src="/assets/images/invoice-add-icon.svg" alt="" /> Add New
            </button>
          </div>
        )}

        {shouldShow("telecom_support") && (
          <div className="invoice-section-div">
            <h3 className="d-flex align-items-center gap-2">
              <img src="/assets/images/invoice-user-icon.svg" alt="" />
              <span className="font-16 font-weight-bold heading-color">
                Telecom Support
              </span>
            </h3>
            <Row>
              <Col lg={4}>
                <Form.Group className="mb-3">
                  <Form.Label className="label-input">Title</Form.Label>
                  <Form.Control
                    type="text"
                    value={telecomTitle}
                    onChange={(event) => setTelecomTitle(event.target.value)}
                    isInvalid={Boolean(getError("telecom_support", "title"))}
                    className="input-field"
                  />
                  <Form.Control.Feedback type="invalid">{getError("telecom_support", "title")}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col lg={4}>
                <Form.Group className="mb-3">
                  <Form.Label className="label-input">License Rate ($)</Form.Label>
                  <CurrencyInput
                    value={telecomLicenseRate}
                    onChange={(event) => setTelecomLicenseRate(event.target.value)}
                    isInvalid={Boolean(getError("telecom_support", "licenseRate"))}
                  />
                  <Form.Control.Feedback type="invalid">{getError("telecom_support", "licenseRate")}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col lg={4}>
                <Form.Group className="mb-3">
                  <Form.Label className="label-input">No of Licenses</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    value={telecomNumberOfLicenses}
                    onChange={(event) => setTelecomNumberOfLicenses(event.target.value)}
                    isInvalid={Boolean(getError("telecom_support", "numberOfLicenses"))}
                    className="input-field"
                  />
                  <Form.Control.Feedback type="invalid">{getError("telecom_support", "numberOfLicenses")}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
          </div>
        )}

        {shouldShow("telecom_consumption") && (
          <div className="invoice-section-div">
            <h3 className="d-flex align-items-center gap-2">
              <img src="/assets/images/invoice-user-icon.svg" alt="" />
              <span className="font-16 font-weight-bold heading-color">
                Telecom Consumption
              </span>
            </h3>
            <Row>
              <Col lg={4}>
                <Form.Group className="mb-3">
                  <Form.Label className="label-input">Rate ($)</Form.Label>
                  <CurrencyInput
                    value={telecomPerMinuteRate}
                    onChange={(event) => setTelecomPerMinuteRate(event.target.value)}
                    isInvalid={Boolean(
                      getError("telecom_consumption", "rate") ||
                      getError("telecom_consumption", "perMinuteRate")
                    )}
                  />
                  <Form.Control.Feedback type="invalid">{getError("telecom_consumption", "rate") || getError("telecom_consumption", "perMinuteRate")}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col lg={4}>
                <Form.Group className="mb-3">
                  <Form.Label className="label-input">Charge Time Type</Form.Label>
                  <div className="d-flex gap-3">
                    <Form.Check
                      type="radio"
                      id={`${chargeTimeTypeName}-handle`}
                      name={chargeTimeTypeName}
                      label="Handle Time"
                      checked={selectedChargeTimeType === "handle_time"}
                      onChange={() => setTelecomChargeTimeType("handle_time")}
                    />
                    <Form.Check
                      type="radio"
                      id={`${chargeTimeTypeName}-talk`}
                      name={chargeTimeTypeName}
                      label="Talk Time"
                      checked={selectedChargeTimeType === "talk_time"}
                      onChange={() => setTelecomChargeTimeType("talk_time")}
                    />
                  </div>
                  <Form.Control.Feedback type="invalid">{getError("telecom_consumption", "chargeTimeType")}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col lg={4}>
                <Form.Group className="mb-3">
                  <Form.Label className="label-input">Charge Time Unit</Form.Label>
                  <div className="d-flex gap-3">
                    <Form.Check
                      type="radio"
                      id={`${chargeTimeUnitName}-minute`}
                      name={chargeTimeUnitName}
                      label="Charge by Minute"
                      checked={selectedChargeTimeUnit === "minute"}
                      onChange={() => setTelecomChargeTimeUnit("minute")}
                    />
                    <Form.Check
                      type="radio"
                      id={`${chargeTimeUnitName}-hour`}
                      name={chargeTimeUnitName}
                      label="Charge by Hour"
                      checked={selectedChargeTimeUnit === "hour"}
                      onChange={() => setTelecomChargeTimeUnit("hour")}
                    />
                  </div>
                  <Form.Control.Feedback type="invalid">{getError("telecom_consumption", "chargeTimeUnit")}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col lg={4}>
                <Form.Group className="mb-3">
                  <Form.Label className="label-input">Handle Time (Minutes)</Form.Label>
                  <Form.Control
                    type="text"
                    value={telecomHandleTime ?? ""}
                    disabled
                    className="input-field bg-light"
                  />
                </Form.Group>
              </Col>
              <Col lg={4}>
                <Form.Group className="mb-3">
                  <Form.Label className="label-input">Talk Time (Minutes)</Form.Label>
                  <Form.Control
                    type="text"
                    value={telecomTalkTime ?? ""}
                    disabled
                    className="input-field bg-light"
                  />
                </Form.Group>
              </Col>
              <Col lg={4}>
                <Form.Group className="mb-3">
                  <Form.Label className="label-input">
                    {selectedChargeTimeUnit === "hour"
                      ? "Total Hours"
                      : "Total Minutes"}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={totalRuntimeDisplay}
                    disabled
                    className="input-field bg-light"
                  />
                </Form.Group>
              </Col>
            </Row>
          </div>
        )}

        {shouldShow("misc_telecom") && (
          <div className="invoice-section-div">
            <h3 className="d-flex align-items-center gap-2">
              <img src="/assets/images/invoice-user-icon.svg" alt="" />
              <span className="font-16 font-weight-bold heading-color">
                Misc Telecom
              </span>
            </h3>
            {miscTelecomRows.map((row, rowIndex) => (
              <div className="bg-light p-3 border-sm mb-3" key={row.id}>
                <Row>
                  <Col>
                    <Form.Group className="mb-3">
                      <Form.Label className="label-input">Misc Heading</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Type here..."
                        value={row.heading}
                        onChange={handleMiscTelecomFieldChange(row.id, "heading")}
                        isInvalid={Boolean(getError("misc_telecom", "heading", rowIndex))}
                        className="input-field"
                      />
                      <Form.Control.Feedback type="invalid">{getError("misc_telecom", "heading", rowIndex)}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group className="mb-3">
                      <Form.Label className="label-input">Charge ($)</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="$ 0.00"
                        value={row.charge}
                        onChange={handleMiscTelecomFieldChange(row.id, "charge")}
                        isInvalid={Boolean(getError("misc_telecom", "charge", rowIndex))}
                        className="input-field"
                      />
                      <Form.Control.Feedback type="invalid">{getError("misc_telecom", "charge", rowIndex)}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col lg={1} className="text-center">
                    <button
                      type="button"
                      className="border-0 bg-transparent p-0"
                      onClick={() => handleDeleteClick("misc_telecom", row.id)}
                      aria-label="Delete misc telecom row"
                    >
                      <img src="/assets/images/delete-icon.svg" alt="" />
                    </button>
                  </Col>
                </Row>
              </div>
            ))}
            <button
              type="button"
              className="border-0 bg-transparent text-success font-12 font-weight-bold d-flex align-items-center gap-2"
              onClick={handleAddMiscTelecomRow}
            >
              <img src="/assets/images/invoice-add-icon.svg" alt="" /> Add New
            </button>
          </div>
        )}

        {showDeleteModal && (
          <DeleteModal
            show={showDeleteModal}
            onClose={handleCloseDeleteModal}
            onDelete={handleConfirmDelete}
            message="Are you sure you want to delete this row?"
          />
        )}
      </div>
    </div>
  );
};

export default TechnologyTab;